import { Box, Dialog, Drawer } from '@mui/material'
import { Route, Routes } from 'react-router-dom'

import ChangePasswordView from './components/modal/ChangePasswordView'
import ControllerView from './components/sidebar/ControllerView'
import EmitterView from './components/sidebar/EmitterView'
import EtzView from './components/sidebar/EtzView'
import { LocationView } from './components/sidebar/LocationView'
import PipesView from './components/sidebar/PipesView/PipesView'
import PlantView from './components/sidebar/PlantView'
import PumpsView from './components/sidebar/PumpsView'
import SensorView from './components/sidebar/SensorView'
import { SiteView } from './components/sidebar/SiteView'
import SoilView from './components/sidebar/SoilView'
import { StationOverview } from './components/sidebar/StationsView'

import { MenuUI } from '@/components/common/MenuUI'
import DeleteAreaModal from './components/modal/DeleteAreaModal'
import DeleteEmitterModal from './components/modal/DeleteEmitterModal'
import DeleteFlowSensorView from './components/modal/DeleteFlowSensorView'
import DeleteFlowValveView from './components/modal/DeleteFlowValveView'
import DeleteHydraulicModal from './components/modal/DeleteHydraulicView'
import DeletePlantModal from './components/modal/DeletePlantModal'
import DeleteReferenceModal from './components/modal/DeleteReferenceModal'
import DeleteSoilModal from './components/modal/DeleteSoilModal'
import DeleteStationModal from './components/modal/DeleteStationModal'
import NotificationModal from './components/modal/NotificationModal'
import AddSubAreaView from './components/sidebar/AreaView/AddSubAreaView'
import EditSubAreaView from './components/sidebar/AreaView/EditSubAreaView'
import FormAreaView from './components/sidebar/AreaView/FormAreaView'
import EtReferenceView from './components/sidebar/EtReferenceView/EtReferenceView'
import FormSensorView from './components/sidebar/HydraulicRule/FormSensorView'
import FormValveView from './components/sidebar/HydraulicRule/FormValveView'

import NotFound from '@/components/common/NotFound'
import DeleteWaterSourceModal from '@/features/config/components/modal/DeleteWaterSourceView'
import FormWaterSourceView from '@/features/config/components/sidebar/WaterSourceView'
import DeleteFertigationModal from '../operations/components/modal/DeleteFertigationModal'
import DeleteCisFertigationModal from './../operations/components/modal/DeleteCisFertigationModal'
import DeleteFlowRuleModal from './components/modal/DeleteFlowRule'
import DeleteEtzModal from './components/modal/DeleteEtzModal'
import StationModal from './components/modal/StationModal'
import FertigationView from './components/sidebar/FertigationView'
import CisFertigationView from './components/sidebar/CisFertigationView'
import FertigationGlobalExclusion from './components/sidebar/FertigationView/FertigationGlobalExclusion'
import FormHydraulicView from './components/sidebar/HydraulicView'
import StationsView from './components/sidebar/StationsView/StationsView'
import {
  ConfigUIProvider,
  MODAL_VIEW,
  SIDEBAR_VIEW,
  useConfigUI,
} from './context'
import ControllerModal from './modal/ControllerModal'
import AreasAndZones from './pages/AreasAndZones/AreasAndZones'
import Controllers from './pages/Controllers'
import DataConfiguration from './pages/DataConfiguration'
import FertigationConfig from './pages/CisFertigation'
import Emitter from './pages/Emitter'
import EtReference from './pages/EtReference/EtReference'
import EtZone from './pages/EtZone/EtZone'
import Hydraulic from './pages/Hydraulic/Hydraulic'
import HydraulicRuleView from './pages/HydraulicRule/HydraulicRule'
import Plants from './pages/Plants'
import Sensors from './pages/Sensors'
import SitesAndLocations from './pages/SitesAndLocations'
import Soil from './pages/Soil'
import Stations from './pages/Station/Stations'
import { CONFIG_ROUTES } from './routes'
import FormTurfQualityView from './components/sidebar/TurfQuality'
import DeleteTurfQualityModal from './components/modal/DeleteTurfQualityView'
import UpdateCisFertigationModal from '../operations/components/modal/UpdateCisFertigationModal'

import { UserRole } from '@/types/user'

function ConfigContainer() {
  return (
    <ConfigUIProvider>
      <Routes>
        <Route index element={<SitesAndLocations />} />
        <Route
          path={CONFIG_ROUTES.sitesAndLocations.path}
          element={<SitesAndLocations />}
        />
        <Route
          path={CONFIG_ROUTES.areasAndZones.path}
          element={<AreasAndZones />}
        />
        <Route
          path={CONFIG_ROUTES.controllers.path}
          element={<Controllers />}
        />
        <Route path={CONFIG_ROUTES.plants.path} element={<Plants />} />
        <Route path={CONFIG_ROUTES.emitter.path} element={<Emitter />} />
        <Route path={CONFIG_ROUTES.stations.path} element={<Stations />} />
        <Route path={CONFIG_ROUTES.etz.path} element={<EtZone />} />
        <Route path={CONFIG_ROUTES.sensors.path} element={<Sensors />} />
        <Route path={CONFIG_ROUTES.hydraulic.path} element={<Hydraulic />} />

        <Route
          path={CONFIG_ROUTES.hydraulicRule.path}
          element={<HydraulicRuleView />}
        >
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route
          path={CONFIG_ROUTES.etReference.path}
          element={<EtReference />}
        />
        <Route
          path={CONFIG_ROUTES.dataConfig.path}
          element={<DataConfiguration />}
        />
        <Route
          path={CONFIG_ROUTES.fertigation.path}
          element={<FertigationConfig />}
        />
        <Route path={CONFIG_ROUTES.soil.path} element={<Soil />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <MenuLeftUI />
      <SidebarUI />
      <ModalUI />
    </ConfigUIProvider>
  )
}

const SidebarView: React.FC<{
  sidebarView: SIDEBAR_VIEW
  displaySidebar: boolean
  closeSidebar(): any
  isSidebarDismissLocked(): boolean
}> = ({
  sidebarView,
  displaySidebar,
  closeSidebar,
  isSidebarDismissLocked,
}) => {
  return (
    <Drawer
      variant="temporary"
      SlideProps={{
        timeout: { enter: 600, exit: 400 }, // Adjust the duration as needed (in milliseconds)
      }}
      open={displaySidebar}
      anchor="right"
      // SCRUM-1032 QC item 6: a view holding a form takes the dismiss lock, and
      // then only its own Cancel button closes it. Everything else keeps the
      // previous behaviour, where clicking away closes the drawer.
      onClose={() => {
        if (!isSidebarDismissLocked()) closeSidebar()
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {displaySidebar && (
        <Box bgcolor="background.default" color="text.main" flexGrow={1}>
          {sidebarView.type === 'LOCATION_VIEW' && (
            <LocationView
              id={sidebarView?.id}
              name={sidebarView?.name}
              siteId={sidebarView?.siteId}
            />
          )}
          {sidebarView.type === 'CONTROLLERS_VIEW' && (
            <ControllerView
              id={sidebarView?.id}
              address={sidebarView?.address}
              twiId={sidebarView?.twiId}
            />
          )}
          {sidebarView.type === 'PIPES_VIEW' && (
            <PipesView id={sidebarView?.id} />
          )}
          {sidebarView.type === 'PUMPS_VIEW' && (
            <PumpsView id={sidebarView?.id} />
          )}

          {sidebarView.type === 'PLANT_VIEW' && (
            <PlantView id={sidebarView?.id} />
          )}
          {sidebarView.type === 'FERTIGATION_VIEW' && (
            <FertigationView id={sidebarView?.id} />
          )}
          {sidebarView.type === 'CIS_FERTIGATION_VIEW' && (
            <CisFertigationView
              id={sidebarView?.id}
              pipeId={sidebarView?.pipeId}
              isParallelMode={sidebarView?.isParallelMode}
            />
          )}
          {sidebarView.type === 'FERTIGATION_GLOBAL_EXCLUSION' && (
            <FertigationGlobalExclusion id={sidebarView?.id} />
          )}
          {sidebarView.type === 'SITE_VIEW' && (
            <SiteView id={sidebarView?.id} name={sidebarView?.name} />
          )}
          {sidebarView.type === 'STATIONS_VIEW' && (
            <StationsView id={sidebarView?.id} />
          )}
          {sidebarView.type === 'STATIONS_OVERVIEW' && (
            <StationOverview id={sidebarView?.id} />
          )}
          {sidebarView.type === 'ETZ_VIEW' && <EtzView id={sidebarView?.id} />}
          {sidebarView.type === 'SENSOR_VIEW' && (
            <SensorView id={sidebarView?.id} />
          )}

          {sidebarView.type === 'ADD_AREA_VIEW' && (
            <FormAreaView id={Number(sidebarView?.id)} />
          )}
          {sidebarView.type === 'ADD_SUB_AREA_VIEW' && (
            <AddSubAreaView
              id={Number(sidebarView?.id)}
              parentName={sidebarView?.parentName}
            />
          )}
          {sidebarView.type === 'EDIT_SUB_AREA_VIEW' && (
            <EditSubAreaView
              id={Number(sidebarView?.id)}
              parentId={sidebarView?.parentId}
              name={sidebarView?.name}
            />
          )}

          {sidebarView.type === 'HYDRAULIC_VIEW' && (
            <FormHydraulicView
              id={Number(sidebarView?.id)}
              parentId={Number(sidebarView.parentId)}
            />
          )}

          {sidebarView.type === 'FLOW_SENSOR_VIEW' && (
            <FormSensorView
              id={Number(sidebarView?.id)}
              flowRuleId={Number(sidebarView?.flowRuleId)}
            />
          )}
          {sidebarView.type === 'FLOW_VALVE_VIEW' && (
            <FormValveView
              id={Number(sidebarView?.id)}
              flowRuleId={Number(sidebarView?.flowRuleId)}
              initialData={sidebarView?.data}
            />
          )}

          {sidebarView.type === 'EMITTER_VIEW' && (
            <EmitterView id={sidebarView?.id} />
          )}

          {sidebarView.type === 'HISTORICAL_VIEW' && (
            <EtReferenceView id={sidebarView?.id} />
          )}
          {sidebarView.type === 'SOIL_VIEW' && (
            <SoilView id={sidebarView?.id} />
          )}

          {sidebarView.type === 'WATERSOURCE_VIEW' && (
            <FormWaterSourceView
              id={Number(sidebarView?.id)}
              parentId={Number(sidebarView.parentId)}
            />
          )}
          {sidebarView.type === 'TURFQUALITY_VIEW' && (
            <FormTurfQualityView
              id={Number(sidebarView?.id)}
              parentId={Number(sidebarView.parentId)}
            />
          )}
        </Box>
      )}
    </Drawer>
  )
}

// Exported so the guided Common Data setup (SetUpGuided) can reuse the full config
// drawer/modal chrome instead of re-implementing it per step (SCRUM-1032).
export const SidebarUI: React.FC = () => {
  const {
    displaySidebar,
    closeSidebar,
    sidebarView,
    isSidebarDismissLocked,
  } = useConfigUI()
  return (
    <SidebarView
      sidebarView={sidebarView}
      displaySidebar={displaySidebar}
      closeSidebar={closeSidebar}
      isSidebarDismissLocked={isSidebarDismissLocked}
    />
  )
}

const ModalView: React.FC<{
  modalView: MODAL_VIEW
  displayModal: boolean
  closeModal(): any
}> = ({ modalView, displayModal, closeModal }) => {
  return (
    <Dialog
      open={displayModal}
      onClose={closeModal}
      maxWidth={modalView.maxWidth}
    >
      {modalView.type === 'CHANGE_PASSWORD_VIEW' && <ChangePasswordView />}
      {modalView.type === 'DELETE_FERTIGATION' && (
        <DeleteFertigationModal
          interfaceId={modalView.interfaceId}
          id={modalView.id}
        />
      )}
      {modalView.type === 'DELETE_CIS_FERTIGATION' && (
        <DeleteCisFertigationModal
          interfaceId={modalView.interfaceId}
          id={modalView.id}
        />
      )}
      {modalView.type === 'UPDATE_MODE_CIS_FERTIGATION' && (
        <UpdateCisFertigationModal
          interfaceId={modalView.interfaceId}
          pipeId={modalView.pipeId}
          fertigationMode={modalView.fertigationMode}
        />
      )}

      {modalView.type === 'STATION_MODAL' && (
        <StationModal
          id={modalView.id}
          stateStation={modalView.stateStation}
          onToggleSuccess={() => modalView.onSuccess?.()}
        />
      )}

      {modalView.type === 'CONTROLLER_MODAL' && (
        <ControllerModal
          text={modalView.data?.text}
          onClose={modalView.data?.onClose}
        />
      )}
      {modalView.type === 'DELETE_PLANT_VIEW' && (
        <DeletePlantModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
          onSuccess={modalView.onSuccess}
        />
      )}
      {modalView.type === 'DELETE_SOIL_VIEW' && (
        <DeleteSoilModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
          onSuccess={modalView.onSuccess}
        />
      )}
      {modalView.type === 'DELETE_EMITTER_VIEW' && (
        <DeleteEmitterModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
          onSuccess={modalView.onSuccess}
        />
      )}
      {modalView.type === 'DELETE_ETZ_VIEW' && (
        <DeleteEtzModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          onSuccess={modalView.onSuccess}
        />
      )}
      {modalView.type === 'DELETE_AREA_VIEW' && (
        <DeleteAreaModal
          id={Number(modalView.id)}
          interfaceId={modalView.interfaceId}
          onSuccess={modalView.onSuccess}
          mode={modalView.mode}
          name={modalView.name}
        />
      )}
      {modalView.type === 'DELETE_REFERENCE_VIEW' && (
        <DeleteReferenceModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
          onSuccess={modalView.onSuccess}
        />
      )}

      {modalView.type === 'DELETE_STATION_VIEW' && (
        <DeleteStationModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          onSuccess={modalView.onSuccess}
          name={modalView.name}
        />
      )}
      {modalView.type === 'DELETE_HYDRAULIC_VIEW' && (
        <DeleteHydraulicModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
          flowRuleId={modalView.flowRuleId}
          subPipeCount={modalView.subPipeCount}
          onSuccess={modalView.onSuccess}
        />
      )}
      {modalView.type === 'DELETE_FLOW_SENSOR_VIEW' && (
        <DeleteFlowSensorView
          id={modalView.id}
          interfaceId={modalView.interfaceId}
        />
      )}
      {modalView.type === 'DELETE_FLOW_VALVE_VIEW' && (
        <DeleteFlowValveView
          id={modalView.id}
          interfaceId={modalView.interfaceId}
        />
      )}
      {modalView.type === 'DELETE_FLOW_RULE_VIEW' && (
        <DeleteFlowRuleModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
        />
      )}
      {modalView.type === 'NOTIFICATION_MODAL' && (
        <NotificationModal data={modalView.data} />
      )}

      {modalView.type === 'DELETE_WATER_SOURCE_VIEW' && (
        <DeleteWaterSourceModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
          name={modalView.name}
        />
      )}

      {modalView.type === 'DELETE_TURF_QUALITY_VIEW' && (
        <DeleteTurfQualityModal
          id={modalView.id}
          interfaceId={modalView.interfaceId}
        />
      )}
    </Dialog>
  )
}

// Exported for reuse by the guided Common Data setup (SCRUM-1032) — see SidebarUI above.
export const ModalUI: React.FC = () => {
  const { displayModal, closeModal, modalView } = useConfigUI()

  return displayModal ? (
    <ModalView
      modalView={modalView}
      closeModal={closeModal}
      displayModal={displayModal}
    />
  ) : null
}

const MenuLeftUI: React.FC = () => {
  const { displayMenu, closeMenu } = useConfigUI()

  return <MenuUI closeMenu={closeMenu} displayMenu={displayMenu} />
}

export default ConfigContainer
